// Small shared components: icons, toasts, modal, confirm dialog, row menu, dates.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

const PATHS: Record<string, string> = {
  clients: 'M3 21V7l9-4 9 4v14M9 21v-6h6v6M3 21h18',
  users: 'M16 19c0-2.8-2.2-4-4-4s-4 1.2-4 4M12 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6M20 19c0-2-1.2-3.2-2.8-3.7M17 6.3a3 3 0 0 1 0 5.4',
  audit: 'M9 5H6v16h12V5h-3M9 3h6v4H9zM9 12h6M9 16h4',
  plus: 'M12 5v14M5 12h14',
  out: 'M15 4h4v16h-4M10 8l-4 4 4 4M6 12h11',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM20 20l-4-4',
  shield: 'M12 3 4 6v6c0 4.5 3.4 8 8 9 4.6-1 8-4.5 8-9V6ZM8.5 12l2.5 2.5 4.5-5',
  x: 'M6 6l12 12M18 6 6 18',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3 12h18M12 3c2.5 3 2.5 15 0 18M12 3c-2.5 3-2.5 15 0 18',
  copy: 'M8 8h12v12H8zM16 8V4H4v12h4',
  folder: 'M3 6h6l2 2h10v11H3z',
  box: 'M12 3 3 7.5v9L12 21l9-4.5v-9ZM3 7.5l9 4.5 9-4.5M12 12v9',
  edit: 'M4 20h4L19 9l-4-4L4 16zM14 6l4 4',
  board: 'M4 4h4v16H4zM10 4h4v10h-4zM16 4h4v13h-4z',
  list: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 13.5l1.6 1.2-2 3.4-1.9-.7a7 7 0 0 1-2.1 1.2L14.7 21h-4l-.3-2.4a7 7 0 0 1-2.1-1.2l-1.9.7-2-3.4 1.6-1.2a7 7 0 0 1 0-2.4L4.4 9.9l2-3.4 1.9.7a7 7 0 0 1 2.1-1.2L10.7 3h4l.3 2.4a7 7 0 0 1 2.1 1.2l1.9-.7 2 3.4-1.6 1.2a7 7 0 0 1 0 2.4Z',
  bug: 'M9 7a3 3 0 0 1 6 0M8 9h8v6a4 4 0 0 1-8 0zM4 13h4M16 13h4M5 8l3 2M19 8l-3 2M5 19l3-2M19 19l-3-2M12 11v8',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  bell: 'M6 16V11a6 6 0 1 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0',
  check: 'M4 4h16v16H4zM8 12l3 3 5-6',
  pipeline: 'M3 5h18l-7 8v6l-4 2v-8z',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  send: 'M4 12 20 4l-6 16-3-7zM11 13l9-9',
  spark: 'M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6',
};

export function Icon({ name, className = 'ico' }: { name: keyof typeof PATHS | string; className?: string }) {
  return <svg className={className} viewBox="0 0 24 24" aria-hidden="true"><path d={PATHS[name] ?? ''} /></svg>;
}

// ---------- toasts ----------
interface Toast { id: number; message: string; bad?: boolean }
const ToastContext = createContext<(message: string, bad?: boolean) => void>(() => {});
let seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = (id: number) => setToasts((t) => t.filter((x) => x.id !== id));
  const show = useCallback((message: string, bad = false) => {
    const id = ++seq;
    setToasts((t) => [...t, { id, message, bad }]);
    window.setTimeout(() => dismiss(id), bad ? 7000 : 4000);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => <div key={t.id} className={`toast ${t.bad ? 'bad' : ''}`} onClick={() => dismiss(t.id)}>{t.message}</div>)}
      </div>
    </ToastContext.Provider>
  );
}
export const useToast = () => useContext(ToastContext);

// ---------- modal ----------
export function Modal({ title, children, onClose, width }: { title: string; children: ReactNode; onClose: () => void; width?: number }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card modal stack" role="dialog" aria-modal="true" aria-label={title} style={width ? { width: `min(${width}px, 100%)` } : undefined}>
        <div className="between"><h2>{title}</h2><button className="btn ghost sm" onClick={onClose} aria-label="Close"><Icon name="x" /></button></div>
        {children}
      </div>
    </div>
  );
}

export function useConfirm() {
  const [state, setState] = useState<{ title: string; body?: string; ok: string; danger?: boolean; resolve: (v: boolean) => void } | null>(null);
  const confirm = (title: string, opts: { body?: string; ok?: string; danger?: boolean } = {}) =>
    new Promise<boolean>((resolve) => setState({ title, body: opts.body, ok: opts.ok ?? 'OK', danger: opts.danger, resolve }));
  const close = (v: boolean) => { state?.resolve(v); setState(null); };
  const dialog = state && (
    <Modal title={state.title} onClose={() => close(false)} width={440}>
      {state.body && <p className="muted" style={{ margin: 0 }}>{state.body}</p>}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn ghost" onClick={() => close(false)}>Cancel</button>
        <button className={`btn ${state.danger ? 'danger solid' : 'primary'}`} onClick={() => close(true)} autoFocus>{state.ok}</button>
      </div>
    </Modal>
  );
  return { confirm, dialog };
}

// ---------- dates ----------
export function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 86400 * 14) return `${Math.floor(s / 86400)} d ago`;
  return formatDate(iso);
}
export const formatDate = (iso: string) => new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
export const formatDateTime = (iso: string) => new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
export const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');

export function CopyField({ value }: { value: string }) {
  const toast = useToast();
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); toast('Copied'); } catch { toast('Select the text and copy it', true); }
  };
  return (
    <div className="row">
      <input className="input mono" value={value} readOnly onFocus={(e) => e.currentTarget.select()} />
      <button className="btn" onClick={copy}><Icon name="copy" />Copy</button>
    </div>
  );
}

// ---------- row menu ----------
export function Menu({ label, items }: { label: string; items: { label: string; onClick: () => void; danger?: boolean }[] }) {
  // Fixed to the viewport so a scrolling table can't clip it.
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const open = pos !== null;
  const setOpen = (v: boolean) => {
    const r = ref.current?.getBoundingClientRect();
    setPos(v && r ? { position: 'fixed', right: innerWidth - r.right, ...(r.bottom + 160 > innerHeight ? { bottom: innerHeight - r.top + 4 } : { top: r.bottom + 4 }) } : null);
  };
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => { if (!ref.current?.contains(e.target as Node)) setPos(null); };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setPos(null);
    const gone = () => setPos(null);
    document.addEventListener('pointerdown', close); document.addEventListener('keydown', esc);
    addEventListener('scroll', gone, true); addEventListener('resize', gone);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', esc); removeEventListener('scroll', gone, true); removeEventListener('resize', gone); };
  }, [open]);
  return (
    <div className="menu-wrap" ref={ref}>
      <button className="btn sm ghost" aria-label={label} aria-expanded={open} onClick={() => setOpen(!open)}>&middot;&middot;&middot;</button>
      {pos && <div className="menu" role="menu" style={pos}>{items.map((i) => <button key={i.label} role="menuitem" className={i.danger ? 'danger' : ''} onClick={() => { setOpen(false); i.onClick(); }}>{i.label}</button>)}</div>}
    </div>
  );
}
